import { Component, OnInit, Input, Output, ChangeDetectionStrategy, EventEmitter, OnDestroy, ViewChild, ElementRef } from '@angular/core';

import { LoggerService } from '@common/modules/core/services/logger/logger.service';
import { UIActionIcon, ISubscriptions } from '@common/modules/insights/interfaces';
import { InsightsCommonUtilsService } from '@common/modules/insights-common/insights-common-utils.service';
import { TranslateHelperService } from '@common/modules/translation/services/translate-helper.service';
import { getKeyCode, KeyCode } from '@common/modules/utils/event';
import { SessionStorageService } from '@common/modules/shared/services/session-storage.service';

import { resources } from './resources';

@Component({
  selector: 'app-vi-upload-container',
  templateUrl: './upload-container.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./upload-container.component.scss'],
  standalone: false
})
export class UploadContainerComponent implements OnInit, OnDestroy {
  // Disable upload file
  @Input() public disabled: boolean;
  // Text
  @Input() public text: string;
  // Upload button selector
  @Input() public uploadSelector: string;
  // Icon next to text
  @Input() public icon: UIActionIcon;
  // Url to upload to
  @Input() public url: string;
  // Allowed multiple files
  @Input() public isMultiple: boolean;
  // File type
  @Input() public filesType: string;
  // Is error after uploading
  @Input() public isError: boolean;
  // Is on progress
  @Input() public isProgress: boolean;
  // Max files size
  @Input() public maxSize?: number;
  // Controls whether the consent is required or not (default - required)
  @Input() public showConsentCheckbox = true;
  // Emit action when upload files
  @Output() public uploadFiles = new EventEmitter();

  @ViewChild('fileInput', { static: false }) public fileInput: ElementRef;

  public uploadedFiles = [];
  public resources = resources;
  public chooseFileBtn: Element;
  public approvedConsent = false;

  private subscriptions: ISubscriptions = {};

  constructor(
    private translateService: TranslateHelperService,
    private insightsCommonUtilsService: InsightsCommonUtilsService,
    private logger: LoggerService,
    private sessionStorageService: SessionStorageService
  ) {
    this.subscriptions.translateActionsResources = this.translateService.translateResources(this.resources).subscribe(() => {});
    this.stimulateUploadClick = this.stimulateUploadClick.bind(this);
  }

  public ngOnInit() {
    this.isProgress = false;
    this.approvedConsent = !this.showConsentCheckbox;

    // Add keyup enter on choose file to stimulate upload
    setTimeout(() => {
      this.chooseFileBtn = document.querySelector(this.uploadSelector);
      if (this.chooseFileBtn) {
        this.chooseFileBtn.addEventListener('keyup', this.stimulateUploadClick);
      }
    }, 100);

    if (this.sessionStorageService.getItem('uploadConsent') === 'true') {
      this.approvedConsent = true;
    }
  }

  public isDisabled() {
    return this.disabled || !this.approvedConsent;
  }

  public toggleConfirmTerms() {
    this.approvedConsent = !this.approvedConsent;
    this.sessionStorageService.setItem('uploadConsent', this.approvedConsent.toString());
  }

  public onSelect($event) {
    // List uploaded files
    for (const file of $event.target.files) {
      this.uploadedFiles.push(file);
    }
    // Fire files list to parent component
    this.uploadFiles.emit(this.uploadedFiles);

    // Display progress bar
    this.isProgress = true;
  }

  public onError($event) {
    this.logger.log('error');
  }

  public ngOnDestroy() {
    // Unsubscribe all subscriptions.
    this.insightsCommonUtilsService.unsubscribeSubscriptions(this.subscriptions);

    if (this.chooseFileBtn) {
      this.chooseFileBtn.removeEventListener('keyup', this.stimulateUploadClick);
    }
  }

  private stimulateUploadClick(event: KeyboardEvent) {
    try {
      const keyCode = getKeyCode(event);

      if (keyCode === KeyCode.Enter) {
        const uploadButton: HTMLInputElement = this.fileInput.nativeElement;
        if (uploadButton) {
          // Stimulate mouse event because file chooser dialog can only be shown with a user activation
          const mouseEvent = new MouseEvent('click', {
            view: window,
            bubbles: true,
            cancelable: false
          });
          uploadButton.dispatchEvent(mouseEvent);
        }
      }
    } catch (error) {
      this.logger.log('Please add key to dictionary.', error);
    }
  }
}
